import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Buildings, FilePdf, Plus, ArrowUpRight, UploadSimple,
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import BatimentNode from './arborescence/BatimentNode';
import BatimentFormDialog from './arborescence/BatimentFormDialog';
import PlanPdfUploadDialog from './arborescence/PlanPdfUploadDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PlansArborescence({ chantierId }) {
  const [structure, setStructure] = useState(null);
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addBatOpen, setAddBatOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [struct, lotsData] = await Promise.all([
        axios.get(`${API_URL}/api/chantiers/${chantierId}/plans-structure`, { withCredentials: true }),
        axios.get(`${API_URL}/api/chantiers/${chantierId}/marches`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setStructure(struct.data);
      setLots((lotsData.data || []).map(l => ({
        id: l.id || l._id,
        nom: l.nom || l.lot_nom || '',
        lot_code: l.code || l.lot_code || l.numero || '',
        entreprise_nom: l.entreprise_nom || '',
        statut: l.statut || '',
      })));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de chargement');
    } finally { setLoading(false); }
  }, [chantierId]);

  useEffect(() => { reload(); }, [reload]);

  const totalEspaces = useMemo(
    () => (structure?.batiments || []).reduce((s, b) => s + (b.espaces?.length || 0), 0),
    [structure]
  );
  const totalPdfs = useMemo(
    () => (structure?.batiments || []).reduce((s, b) => s + (b.plans?.length || 0), 0)
      + (structure?.plans_non_assignes?.length || 0),
    [structure]
  );

  if (loading) return <div className="p-8 text-center text-sm text-[#9A9A9A]">Chargement de l'arborescence…</div>;

  const batiments = structure?.batiments || [];
  const plansNonAssignes = structure?.plans_non_assignes || [];

  return (
    <div className="space-y-4" data-testid="plans-arborescence">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>
            Arborescence du projet
          </h2>
          <p className="text-xs text-[#6B6B6B] mt-1">
            {batiments.length} bâtiment(s) · {totalEspaces} espace(s) · {totalPdfs} plan(s) PDF
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setUploadOpen(true)} variant="outline" size="sm"
            className="rounded-none gap-2 border-[#1E3A5F] text-[#1E3A5F]"
            data-testid="plans-upload-pdf">
            <UploadSimple size={14} /> Importer un plan PDF
          </Button>
          <Button onClick={() => setAddBatOpen(true)}
            className="bg-[#C75B39] hover:bg-[#D97B5C] rounded-none gap-2"
            data-testid="plans-add-batiment">
            <Plus size={14} /> Ajouter un bâtiment
          </Button>
        </div>
      </div>

      {batiments.length === 0 && (
        <div className="border-2 border-dashed border-[#E0E0DB] p-10 text-center"
          data-testid="plans-arborescence-empty">
          <Buildings size={36} className="mx-auto text-[#9A9A9A] mb-3" />
          <p className="text-sm text-[#6B6B6B] mb-3">
            Aucun bâtiment défini. Pour une maison simple, créez un bâtiment unique
            puis ajoutez vos niveaux et pièces.
          </p>
          <Button onClick={() => setAddBatOpen(true)}
            className="bg-[#C75B39] hover:bg-[#D97B5C] rounded-none gap-2">
            <Plus size={14} /> Créer le premier bâtiment
          </Button>
        </div>
      )}

      <div className="space-y-3">
        {batiments.map(bat => (
          <BatimentNode key={bat.id} batiment={bat} chantierId={chantierId} lots={lots} onChanged={reload} />
        ))}
      </div>

      {plansNonAssignes.length > 0 && (
        <div className="border border-[#F5D5B3] bg-[#FFF8E1] p-3" data-testid="plans-non-assignes">
          <p className="text-xs font-bold uppercase tracking-wider text-[#6B4A24] mb-2 flex items-center gap-1">
            <FilePdf size={12} /> Plans PDF non assignés à un bâtiment ({plansNonAssignes.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {plansNonAssignes.map(p => {
              let url = null;
              if (p.pdf_stored_name) {
                url = `${API_URL}/api/chantiers/${chantierId}/plans/${p.id}/pdf`;
              } else if (p.image_url) {
                url = p.image_url.startsWith('http') ? p.image_url : `${API_URL}${p.image_url}`;
              }
              return (
                <a key={p.id} href={url || '#'} target="_blank" rel="noreferrer"
                  onClick={e => { if (!url) e.preventDefault(); }}
                  className="text-xs px-2 py-1 border border-[#F5D5B3] bg-white hover:border-[#C75B39] flex items-center gap-1.5">
                  <FilePdf size={11} className="text-[#C75B39]" /> {p.nom}
                  <ArrowUpRight size={10} className="text-[#6B6B6B]" />
                </a>
              );
            })}
          </div>
        </div>
      )}

      {addBatOpen && (
        <BatimentFormDialog chantierId={chantierId}
          onSaved={reload} onClose={() => setAddBatOpen(false)} />
      )}

      {uploadOpen && (
        <PlanPdfUploadDialog chantierId={chantierId} batiments={batiments}
          onUploaded={reload} onClose={() => setUploadOpen(false)} />
      )}
    </div>
  );
}
